import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

/**
 * ImageUploader — admin-only, reusable image upload control.
 * Uploads to /api/admin/upload-image, previews the resulting URL,
 * and exposes onChange(newUrl) so parent can persist the URL wherever needed.
 *
 * Props:
 *   label     — visible label above the control
 *   value     — current URL (may be "" or a full https:// URL or "/api/files/...")
 *   onChange  — fn(newUrl: string) => void
 *   kind      — hint for the backend ("hero" | "about" | "careers" | "service" | "fleet" | "image")
 *   testid    — data-testid prefix
 *   help      — small helper hint shown below the previewer
 *   aspect    — CSS aspect-ratio hint for the preview (default "16 / 9")
 */
export default function ImageUploader({
  label,
  value = "",
  onChange,
  kind = "image",
  testid = "image-uploader",
  help = "PNG, JPG, WEBP, SVG or GIF · max 10 MB",
  aspect = "16 / 9",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const preview = value
    ? (value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value)
    : "";

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/upload-image?kind=${encodeURIComponent(kind)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-dashed border-[var(--lt-border)] p-4 bg-[var(--lt-bg)] rounded-xl" data-testid={testid}>
      {label && (
        <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-3 block">{label}</Label>
      )}

      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div
          className="relative w-full md:w-56 rounded-lg overflow-hidden border border-[var(--lt-border)] bg-[var(--lt-panel)] flex-shrink-0"
          style={{ aspectRatio: aspect }}
          data-testid={`${testid}-preview`}
        >
          {preview ? (
            <img src={preview} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs font-mono-lt text-slate-500">
              No image yet
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testid}-input`}
            className="hidden"
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid}-btn`}
              className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-10 px-4 font-head uppercase tracking-widest text-xs"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Uploading…" : (value ? "Replace" : "Upload")}
            </Button>

            {value && (
              <Button
                type="button"
                variant="outline"
                onClick={() => onChange?.("")}
                data-testid={`${testid}-clear`}
                className="rounded-xl border-[var(--lt-border)] bg-transparent hover:bg-[var(--lt-panel-hover)] h-10 px-3 font-head uppercase tracking-widest text-xs"
              >
                <X className="h-4 w-4 mr-2" /> Remove
              </Button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-2">{help}</div>
          {value && (
            <div className="text-[11px] text-slate-500 mt-1 truncate" title={value}>
              {value}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
